import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper, Typography, Box, List, ListItem, ListItemText, Divider,
  CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import axios from '../../auth/axiosConfig';
import { useEditMode } from '../EditMode/EditModeContext';

const formatTimestamp = (ts) => {
  const date = new Date(ts);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const EditHistory = ({ verificationId, onDataRestored }) => {
  const { isEditMode, exitEditMode } = useEditMode();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`/ui/${verificationId}/edit-history`);
      const sorted = (res.data || []).sort((a, b) => {
        // System entries (initial run) always last
        if (!a.editor_email && b.editor_email) return 1;
        if (a.editor_email && !b.editor_email) return -1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setHistory(sorted);
    } catch (err) {
      setError('Failed to load edit history');
    } finally {
      setLoading(false);
    }
  }, [verificationId]);

  useEffect(() => {
    if (!verificationId) return;
    fetchHistory();
  }, [verificationId, fetchHistory, isEditMode]);

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const res = await axios.post(`/ui/${verificationId}/restore/${restoreTarget.id}`);
      if (onDataRestored) onDataRestored(res.data);
      exitEditMode();
      setSnackbar({ severity: 'success', message: 'Restored successfully' });
    } catch (err) {
      setSnackbar({ severity: 'error', message: err.response?.data?.detail || 'Failed to restore' });
    } finally {
      setRestoring(false);
      setRestoreTarget(null);
    }
  };

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Edit history
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {error && <Typography variant="body2" color="error">{error}</Typography>}

      {!loading && !error && history.length === 0 && (
        <Typography variant="body2" color="text.secondary">No edit history</Typography>
      )}

      {!loading && !error && history.length > 0 && (
        <List disablePadding>
          {history.map((entry, idx) => (
            <React.Fragment key={entry.id}>
              {idx > 0 && <Divider />}
              <ListItem sx={{ px: 0, py: 1, alignItems: 'flex-start' }}>
                <ListItemText
                  primary={entry.edit_description || 'Initial verification run'}
                  secondary={
                    <>
                      {entry.editor_email || 'System'}
                      {' — '}
                      {formatTimestamp(entry.created_at)}
                    </>
                  }
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                {isEditMode && entry.has_result_data && !entry.is_restore && (
                  <Button
                    size="small"
                    startIcon={<RestoreIcon />}
                    onClick={() => setRestoreTarget(entry)}
                    sx={{ ml: 1, whiteSpace: 'nowrap' }}
                  >
                    Restore
                  </Button>
                )}
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}

      <Dialog open={!!restoreTarget} onClose={() => setRestoreTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Restore this version?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will restore the verification data to:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
            {restoreTarget?.edit_description || 'Initial verification run'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {restoreTarget?.editor_email || 'System'} — {restoreTarget?.created_at && formatTimestamp(restoreTarget.created_at)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
            Your current state will be saved as a snapshot before restoring.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleRestore} disabled={restoring}>
            {restoring ? 'Restoring...' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>

      {snackbar && (
        <Snackbar open autoHideDuration={4000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>{snackbar.message}</Alert>
        </Snackbar>
      )}
    </Paper>
  );
};

export default EditHistory;
